<template>
  <UISideSheet
    :is-open="isPanelOpen"
    :title="panelTitle"
    :show-back="isViewingTopics"
    width="550px"
    full-height-mobile
    @toggle-side-sheet="tutorialsPanelStore.closePanel()"
    @back="selectedCategoryId = null"
    @closed="handleSheetClosed"
  >
    <div
      data-cy="tutorials-panel"
      class="tutorials-panel-content"
    >
      <TutorialsCategoryList
        v-if="!isViewingTopics"
        @select="selectedCategoryId = $event"
      />
      <TutorialsTopicList
        v-else
        :category-id="selectedCategoryId"
        @select-topic="handleTopicClick"
      />
    </div>
  </UISideSheet>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import {
  onboardingCategories,
  onboardingTopicContentTypes,
  onboardingWalkthroughs,
} from '@/constants/onboardingChecklist';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';
import { useWalkthroughLaunch } from '@/composables/useWalkthroughLaunch';

const tutorialsPanelStore = useTutorialsPanelStore();
const { isPanelOpen } = storeToRefs(tutorialsPanelStore);
const { launchWalkthrough } = useWalkthroughLaunch();

// Two views: the category list, or (once a category is picked) that category's topics.
const selectedCategoryId = ref(null);
const isViewingTopics = computed(() => Boolean(selectedCategoryId.value));

const selectedCategory = computed(() =>
  onboardingCategories.find((category) => category.id === selectedCategoryId.value),
);
const panelTitle = computed(() => selectedCategory.value?.name ?? 'Tutorials');

// Always re-enter on the category view, never mid-drill-down from a previous visit.
watch(isPanelOpen, (open) => {
  if (!open) {
    selectedCategoryId.value = null;
  }
});

// Set by handleTopicClick, consumed by handleSheetClosed — the walkthrough only starts
// once UI/SideSheet.vue's own `closed` event confirms the leave transition actually
// finished, so it never measures target visibility while the sheet still covers the page.
const pendingWalkthrough = ref(null);

/**
 * The 5 static-modal topics have no authored content yet (see CategoryList/TopicList
 * comments) and render disabled, so this only ever runs for a real walkthrough topic.
 */
const handleTopicClick = (topicId) => {
  const isWalkthroughTopic = onboardingTopicContentTypes[topicId] === 'walkthrough';
  const walkthrough = onboardingWalkthroughs.find((candidate) => candidate.id === topicId);

  if (!isWalkthroughTopic || !walkthrough) {
    return;
  }

  pendingWalkthrough.value = walkthrough;
  tutorialsPanelStore.closePanel();
  // Mobile only: MobileNavBar's slide-out menu sits underneath this panel and must
  // also close, or it keeps covering the walkthrough's target once the panel is gone.
  tutorialsPanelStore.requestMobileMenuClose();
};

// `closed` fires on every close (X, overlay, back, a topic pick), not just ours — only
// act when a walkthrough is actually queued.
const handleSheetClosed = () => {
  const walkthrough = pendingWalkthrough.value;
  pendingWalkthrough.value = null;

  if (walkthrough) {
    launchWalkthrough(walkthrough, { source: 'tutorials-panel' });
  }
};
</script>
